import type { U3141ChildLotChain, U3141NodePositions } from '../U3141Service';
import { parseConcordanceEntry, stripPartieSuffixFromLotSegment } from '../components/form/u3141ChildLotChain/U3141ConcordanceUtils';
import {
  U3141_GRAPH_LAYOUT_CHILD_OFFSET_Y,
  U3141_GRAPH_LAYOUT_SIBLING_SPACING_X,
} from './u3141GraphLayoutSpacing';

export interface U3141ChildLotChainCreateLotDetailPayload {
  id?: string;
  lot: string;
  propertyDistrict: string;
  cadastre: string;
  codeDistrict?: string;
  cadastreCode?: string;
  rankCode?: string;
  rankLabel?: string;
  dateEstablishment?: string;
  partieDeLot?: boolean;
  concordance: string[];
  lotState: ('validate' | 'notValidate')[];
  operationType?: string | null;
  operationTypeInfo?: string | null;
  operationTypeInfoEnabled?: boolean;
  corrections?: string[] | null;
}

export interface U3141ChildLotChainCreatePayload {
  childLotState: ('importValidate' | 'importNotValidate')[];
  lotDetail: U3141ChildLotChainCreateLotDetailPayload;
  nodePosition?: { x: number; y: number };
  concordancePlacements?: Record<string, { x: number; y: number }>;
}

export interface U3141MapChildLotChainToCreatePayloadOptions {
  /** Entrées concordance retenues (cochées). */
  selectedConcordanceEntries: string[];
  nodePosition?: { x: number; y: number };
  concordancePlacements?: Record<string, { x: number; y: number }>;
}

/** Normalise corrections (string ou tableau) en dates yyyy-MM-dd. */
export function u3141NormalizeCorrectionsForApi(corrections: unknown): string[] | null {
  if (corrections == null || corrections === '') return null;
  if (Array.isArray(corrections)) {
    const dates = corrections.filter((c): c is string => typeof c === 'string' && c.trim() !== '');
    return dates.length > 0 ? dates : null;
  }
  if (typeof corrections === 'string' && corrections.trim() !== '') {
    return [corrections.trim()];
  }
  return null;
}

function normalizeLotState(lotState: string | undefined): ('validate' | 'notValidate')[] {
  if (lotState === 'validate') return ['validate'];
  return ['notValidate'];
}

/**
 * Mappe un U3141ChildLotChain (formulaire) vers le body POST create enrichi (BL-0661).
 */
export function u3141MapChildLotChainToCreatePayload(
  child: U3141ChildLotChain,
  options: U3141MapChildLotChainToCreatePayloadOptions,
): U3141ChildLotChainCreatePayload {
  const ld = child.lotDetail;
  const district = ld?.district;
  const cadastre = ld?.cadastre;

  const payload: U3141ChildLotChainCreatePayload = {
    childLotState: ['importNotValidate'],
    lotDetail: {
      lot: (ld?.lot ?? '').trim(),
      propertyDistrict: (district?.propertyDistrict ?? '').trim(),
      cadastre: (cadastre?.cadastre ?? '').trim(),
      concordance: [...options.selectedConcordanceEntries],
      lotState: normalizeLotState(ld?.lotState),
      partieDeLot: ld?.partieDeLot === true,
    },
  };

  if (ld?.id) payload.lotDetail.id = ld.id;
  if (district?.codeDistrict) payload.lotDetail.codeDistrict = district.codeDistrict;
  if (cadastre?.cadastreCode) payload.lotDetail.cadastreCode = cadastre.cadastreCode;
  if (cadastre?.rankCode) payload.lotDetail.rankCode = cadastre.rankCode;
  if (cadastre?.rankLabel) payload.lotDetail.rankLabel = cadastre.rankLabel;
  if (ld?.dateEstablishment) payload.lotDetail.dateEstablishment = ld.dateEstablishment;
  if (ld?.operationType != null && String(ld.operationType).trim() !== '') {
    payload.lotDetail.operationType = ld.operationType;
  }
  if (ld?.operationTypeInfo !== undefined) payload.lotDetail.operationTypeInfo = ld.operationTypeInfo;
  if (ld?.operationTypeInfoEnabled !== undefined) {
    payload.lotDetail.operationTypeInfoEnabled = ld.operationTypeInfoEnabled;
  }
  const corrections = u3141NormalizeCorrectionsForApi(ld?.corrections);
  if (corrections) payload.lotDetail.corrections = corrections;

  if (options.nodePosition) payload.nodePosition = options.nodePosition;
  if (options.concordancePlacements && Object.keys(options.concordancePlacements).length > 0) {
    payload.concordancePlacements = options.concordancePlacements;
  }

  return payload;
}

/**
 * Positions initiales des lots concordance lors d'un ajout graphe (clé = entrée concordance).
 * Aligné designer parent/enfant : le lot ajouté reste au point de clic ; chaque concordance est placée en dessous, côte à côte.
 */
export function u3141BuildConcordancePlacements(
  selectedEntries: string[],
  mainPosition: { x: number; y: number },
): Record<string, { x: number; y: number }> {
  const placements: Record<string, { x: number; y: number }> = {};
  const n = selectedEntries.length;
  selectedEntries.forEach((entry, index) => {
    placements[entry] = {
      x: mainPosition.x + (index - (n - 1) / 2) * U3141_GRAPH_LAYOUT_SIBLING_SPACING_X,
      y: mainPosition.y + U3141_GRAPH_LAYOUT_CHILD_OFFSET_Y,
    };
  });
  return placements;
}

/**
 * Après update + lotChainGraph : associe childLotChainId aux positions concordance (match lot segment).
 */
export function u3141MergeGraphNodePositionsAfterAdd(
  mainChildLotChainId: string,
  mainPosition: { x: number; y: number },
  selectedConcordanceEntries: string[],
  graphChildren: U3141ChildLotChain[],
  concordancePlacements: Record<string, { x: number; y: number }>,
): U3141NodePositions {
  const positions: U3141NodePositions = {
    [mainChildLotChainId]: mainPosition,
  };

  for (const entry of selectedConcordanceEntries) {
    const parsed = parseConcordanceEntry(entry);
    const lotKey = stripPartieSuffixFromLotSegment(parsed.lot).toLowerCase();
    const match = graphChildren.find((c) => {
      if (c.id === mainChildLotChainId) return false;
      const lot = stripPartieSuffixFromLotSegment(c.lotDetail?.lot ?? '').toLowerCase();
      return lot === lotKey;
    });
    const placement = concordancePlacements[entry];
    if (match?.id && placement) {
      positions[match.id] = placement;
    }
  }

  return positions;
}

